import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus, X, Users, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name: "", animal: "", slug: "", personality: "", quote: "", short_desc: "", long_desc: "", color: "#F58CC8", price: 79, availability: "in_stock", image: "", story_categories: [], favorite_things: [], features: [], sku: "", inventory: 0, status: "launched" };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [buddies, setBuddies] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("buddies");

  const load = () => {
    api.get("/buddies").then((r) => setBuddies(r.data)).catch(() => {});
    api.get("/newsletter").then((r) => setSubscribers(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
    if (user?.role === "admin") load();
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== "admin") return <div className="bg-cream py-32 text-center text-navy/40">Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: Number(editing.price), inventory: Number(editing.inventory) };
    try {
      if (editing.id) await api.put(`/buddies/${editing.id}`, payload);
      else await api.post("/buddies", payload);
      toast.success(editing.id ? "Buddy updated" : "New Buddy added");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Buddy?")) return;
    await api.delete(`/buddies/${id}`);
    toast.success("Buddy removed");
    load();
  };

  const inputCls = "w-full rounded-xl border border-blush bg-cream px-4 py-2.5 text-sm text-navy placeholder:text-navy/35 focus:border-pink focus:outline-none";

  return (
    <div className="bg-cream" data-testid="admin-page">
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-3xl font-medium text-navy">Buddy HQ</h1>
          <div className="flex gap-2">
            {[["buddies", ShoppingBag, "Buddies"], ["subscribers", Users, "Subscribers"]].map(([t, Icon, label]) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-sm font-semibold transition ${tab === t ? "bg-pink text-navy" : "bg-white text-navy/50"}`}>
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "buddies" && (
          <>
            <button onClick={() => setEditing({ ...EMPTY })} data-testid="admin-add-buddy"
              className="mt-8 flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 font-display text-sm font-semibold text-white transition hover:scale-105">
              <Plus size={15} /> New Buddy
            </button>
            <div className="mt-6 overflow-x-auto rounded-[1.5rem] bg-white shadow-[0_10px_40px_rgba(16,42,86,0.06)]" data-testid="admin-buddy-table">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="bg-blush/50 text-xs uppercase tracking-widest text-navy/45">
                  <tr><th className="px-5 py-4">Buddy</th><th className="px-5 py-4">Price</th><th className="px-5 py-4">Stock</th><th className="px-5 py-4">Status</th><th className="px-5 py-4"></th></tr>
                </thead>
                <tbody className="divide-y divide-blush/60">
                  {buddies.map((b) => (
                    <tr key={b.id} className="text-navy/70" data-testid={`admin-row-${b.slug}`}>
                      <td className="flex items-center gap-3 px-5 py-3.5">
                        <img src={b.image} alt="" className="h-10 w-8 rounded-lg object-cover" />
                        <span className="font-semibold text-navy">{b.name} the {b.animal}</span>
                      </td>
                      <td className="px-5 py-3.5">${b.price.toFixed(2)}</td>
                      <td className="px-5 py-3.5">{b.inventory}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${b.status === "launched" ? "bg-moon/40 text-navy" : "bg-lavender text-navy/60"}`}>{b.status}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-2">
                          <button onClick={() => setEditing(b)} aria-label={`Edit ${b.name}`} data-testid={`admin-edit-${b.slug}`} className="rounded-full bg-blush/60 p-2 text-navy/60 hover:text-pinkdeep"><Pencil size={14} /></button>
                          <button onClick={() => remove(b.id)} aria-label={`Delete ${b.name}`} data-testid={`admin-delete-${b.slug}`} className="rounded-full bg-blush/60 p-2 text-navy/60 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "subscribers" && (
          <div className="mt-8 space-y-6">
            <div className="rounded-[1.5rem] bg-white p-6 shadow-[0_10px_40px_rgba(16,42,86,0.06)]" data-testid="admin-subscribers">
              <h2 className="font-display text-xl font-semibold text-navy">Newsletter family ({subscribers.length})</h2>
              <ul className="mt-4 divide-y divide-blush/60">
                {subscribers.map((s) => (
                  <li key={s.id} className="flex justify-between py-2.5 text-sm text-navy/60">
                    <span>{s.email}</span><span className="text-navy/35">{new Date(s.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
                {subscribers.length === 0 && <li className="py-4 text-sm text-navy/35">No subscribers yet.</li>}
              </ul>
            </div>
            <div className="rounded-[1.5rem] bg-white p-6 shadow-[0_10px_40px_rgba(16,42,86,0.06)]" data-testid="admin-orders">
              <h2 className="font-display text-xl font-semibold text-navy">Orders ({orders.length})</h2>
              <ul className="mt-4 divide-y divide-blush/60">
                {orders.map((o) => (
                  <li key={o.id} className="flex justify-between py-2.5 text-sm text-navy/60">
                    <span>#{o.id.slice(-8).toUpperCase()} — {o.name}</span><span className="font-bold text-pinkdeep">${o.total.toFixed(2)}</span>
                  </li>
                ))}
                {orders.length === 0 && <li className="py-4 text-sm text-navy/35">No orders yet.</li>}
              </ul>
            </div>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/40 p-4 backdrop-blur-sm" data-testid="admin-edit-modal">
            <form onSubmit={save} className="max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto rounded-[2rem] bg-white p-8 shadow-2xl">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-semibold text-navy">{editing.id ? `Edit ${editing.name}` : "New Buddy"}</h2>
                <button type="button" onClick={() => setEditing(null)} aria-label="Close editor" data-testid="admin-edit-close" className="text-navy/40"><X size={20} /></button>
              </div>
              {[["name", "Name"], ["animal", "Animal"], ["slug", "Slug (url)"], ["personality", "Personality"], ["quote", "Bedtime quote"], ["short_desc", "Short description"], ["image", "Image path"], ["sku", "SKU"]].map(([k, label]) => (
                <input key={k} required={["name", "animal", "slug"].includes(k)} placeholder={label} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                  aria-label={label} data-testid={`admin-field-${k}`} className={inputCls} />
              ))}
              <div className="grid grid-cols-3 gap-3">
                <input type="number" step="0.01" placeholder="Price" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                  aria-label="Price" data-testid="admin-field-price" className={inputCls} />
                <input type="number" placeholder="Inventory" value={editing.inventory} onChange={(e) => setEditing({ ...editing, inventory: e.target.value })}
                  aria-label="Inventory" data-testid="admin-field-inventory" className={inputCls} />
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value, availability: e.target.value === "launched" ? "in_stock" : "coming_soon" })}
                  aria-label="Status" data-testid="admin-field-status" className={inputCls}>
                  <option value="launched">Launched</option>
                  <option value="coming_soon">Coming soon</option>
                </select>
              </div>
              <button type="submit" data-testid="admin-save-btn"
                className="w-full rounded-full bg-pink py-3.5 font-display font-semibold text-navy transition hover:bg-moon">
                SAVE BUDDY
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
